import React, { useEffect, useState } from 'react'
import { fetchCollection, getLocalized } from '../lib/cms'
import { Star, MessageSquare, Quote, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { updateMeta } from '../lib/utils'

export default function ReviewsPage() {
  const [testimonials, setTestimonials] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    updateMeta(
      "Kundeanmeldelser | Thellefsen Regnskab",
      "Læs anmeldelser og udtalelser fra vores kunder. Se hvorfor selvstændige vælger Peter Thellefsen som deres faste revisor og samarbejdspartner."
    )
    async function loadData() {
      try {
        const data = await fetchCollection('testimonials')
        setTestimonials(data)
      } catch (error) {
        console.error('Error loading testimonials page data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      {/* Header Section */}
      <section className="bg-white border-b border-stone-200 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 tracking-tight">
            Kunderne siger
          </h1>
          <p className="text-lg sm:text-xl text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Læs anmeldelser fra selvstændige og virksomheder, der har valgt Peter og{' '}
            <Link to="/" className="text-stone-900 font-semibold hover:text-orange-600 transition-colors underline decoration-stone-300 underline-offset-2">
              Thellefsen Regnskab
            </Link>{' '}
            som deres faste revisor og sparringspartner.
          </p>

          {/* Large Trust Badge */}
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-stone-50 border border-stone-200 rounded-none p-6 mt-6">
            <div className="flex flex-col items-center sm:items-start">
              <div className="flex items-center gap-0.5 text-[#18724a]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 fill-current" />
                ))}
              </div>
              <div className="text-2xl font-bold text-stone-900 mt-1">4,76 ud af 5 stjerner</div>
            </div>
            <div className="hidden sm:block h-12 w-px bg-stone-300" />
            <div className="text-center sm:text-left text-sm text-stone-600">
              Baseret på <span className="font-bold text-stone-900">153 bedømmelser</span> på Ageras.<br />
              Opdateret senest maj 2026.
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-48 bg-stone-100 rounded-none animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((testimonial) => (
                <div 
                  key={testimonial.id}
                  className="relative rounded-none border border-stone-200 bg-white p-8 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col justify-between"
                >
                  {/* Quote Icon Background */}
                  <div className="absolute right-8 top-8 text-stone-100 -z-0 pointer-events-none">
                    <Quote className="h-16 w-16 fill-current" />
                  </div>

                  <div className="space-y-4 relative z-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-0.5 text-[#18724a]">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-current" />
                        ))}
                      </div>
                      <span className="text-xs text-stone-400">{testimonial.date}</span>
                    </div>
                    <p className="text-stone-700 italic leading-relaxed text-base">
                      "{getLocalized(testimonial.text)}"
                    </p>
                  </div>

                  <div className="mt-8 pt-6 border-t border-stone-100 flex items-center gap-3 relative z-10">
                    <div>
                      <div className="font-semibold text-stone-900 text-sm">{testimonial.name}</div>
                      <div className="text-xs text-stone-500">{testimonial.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Link to Ageras */}
          <div className="mt-12 text-center">
            <a
              href="https://www.ageras.dk/profil/thellefsen-regnskab-aps-225909"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-none border border-stone-300 bg-white px-6 py-3 text-sm font-semibold text-stone-700 shadow-sm hover:bg-stone-50 hover:text-stone-900 transition-all duration-200"
            >
              Læs alle anmeldelser på Ageras
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative overflow-hidden py-16 bg-orange-600 text-white text-center">
        {/* Diagonal watermark / half white side */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -right-1/4 -bottom-1/4 w-3/4 h-[150%] bg-white/10 transform -rotate-12 origin-bottom-right" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-6">
          <MessageSquare className="h-12 w-12 mx-auto text-white" />
          <h2 className="text-3xl font-bold tracking-tight">Vil du også have ro i maven omkring dit regnskab?</h2>
          <p className="text-white/90 max-w-xl mx-auto leading-relaxed">
            Lad os tage en uforpligtende snak om din virksomhed og finde den helt rigtige løsning til en fast pris.
          </p>
          <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-none bg-stone-900 px-8 py-4 text-base font-semibold text-white shadow-sm hover:bg-stone-800 transition-all duration-200"
            >
              Book gratis samtale nu
            </Link>
            <Link
              to="/ydelser"
              className="inline-flex items-center justify-center rounded-none bg-white text-stone-900 px-7 py-4 text-base font-semibold shadow-sm hover:bg-stone-100 transition-all duration-200"
            >
              Se vores ydelser & priser
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
